import { useEffect, useState } from 'react';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { useTranslation } from '@/i18n';
import CTASection from '@/components/sections/CTASection';
import Hero from '@/components/sections/Hero';
import HowItWorksSection from '@/components/sections/HowItWorksSection';
import MuscleMemorySection from '@/components/sections/MuscleMemorySection';
import ProofBar from '@/components/sections/ProofBar';
import StickyMobileCta from '@/components/sections/StickyMobileCta';
import TokenSavingsSection from '@/components/sections/TokenSavingsSection';
import UseCasesSection from '@/components/sections/UseCasesSection';
import WhyAletiqSection from '@/components/sections/WhyAletiqSection';
import WorksWithSection from '@/components/sections/WorksWithSection';

export default function HomePage() {
  const { t } = useTranslation();
  useDocumentTitle({ title: t.meta.title, description: t.meta.description });
  const [isScrolledFromTop, setIsScrolledFromTop] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setIsScrolledFromTop(window.scrollY > 18);
    };

    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Aletiq',
      description: t.meta.description,
      applicationCategory: 'DeveloperApplication',
      applicationSubCategory: 'AI Agent Verification',
      operatingSystem: 'Any',
      url: 'https://aletiq.ai',
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'USD',
        lowPrice: '0',
        highPrice: '149',
        offerCount: 4,
      },
      featureList: [
        'Automated AI agent verification',
        'Reproducible test workflows',
        'MCP integration (Claude Code, Cursor, Codex)',
        'Local runner execution',
        'Free unlimited re-runs',
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    script.setAttribute('data-schema', 'home-software');
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [t.meta.description]);

  return (
    <div className="relative bg-[#f2f5fa]">
      <div
        className={`relative z-20 mx-auto transition-all duration-1000 ease-out ${
          isScrolledFromTop ? 'max-w-none px-1 pt-1' : 'max-w-none px-0 pt-0'
        }`}
      >
        <div
          className={`overflow-hidden bg-[#f2f5fa] transition-all duration-1000 ease-out ${
            isScrolledFromTop
              ? 'rounded-[18px] border border-slate-300/10 shadow-[0_8px_40px_rgba(2,8,24,0.25)]'
              : 'rounded-none border border-transparent shadow-none'
          }`}
        >
          <Hero />
        </div>
      </div>

      <div className="relative z-10 bg-[#f2f5fa]">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 -top-24 h-24 bg-[#f2f5fa]"
        />
        <ProofBar />
        <MuscleMemorySection />
        <TokenSavingsSection />
        <WhyAletiqSection />
        <HowItWorksSection />
        <UseCasesSection />
        <WorksWithSection />
        <CTASection />
      </div>

      <StickyMobileCta />
    </div>
  );
}
